<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="mb-8">
        <div class="flex items-center gap-4 mb-4">
          <button 
            @click="goBack"
            class="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
            </svg>
            Tillbaka
          </button>
        </div>
        <h1 class="text-3xl font-bold text-gray-900 mb-2">Redigera Evenemang</h1>
        <p class="text-gray-600">Uppdatera information om ditt evenemang</p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600"></div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
        <div class="flex">
          <div class="flex-shrink-0">
            <span class="text-red-400">⚠️</span>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Ett fel uppstod</h3>
            <p class="text-sm text-red-700 mt-1">{{ error }}</p>
          </div>
        </div>
      </div>

      <!-- Form -->
      <form v-else @submit.prevent="updateEvent" class="space-y-6">
        <!-- Event Title -->
        <div>
          <label for="title" class="block text-sm font-medium text-gray-700 mb-2">
            Evenemangstitel *
          </label>
          <input
            id="title"
            v-model="form.title"
            type="text"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Ange evenemangstitel"
          />
        </div>

        <!-- Event Description -->
        <div>
          <label for="description" class="block text-sm font-medium text-gray-700 mb-2">
            Beskrivning *
          </label>
          <textarea
            id="description"
            v-model="form.description"
            rows="4"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Beskriv ditt evenemang"
          ></textarea>
        </div>

        <!-- Event Date -->
        <div>
          <label for="date" class="block text-sm font-medium text-gray-700 mb-2">
            Datum och tid *
          </label>
          <input
            id="date"
            v-model="form.date"
            type="datetime-local"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        <!-- Event Location -->
        <div>
          <label for="location" class="block text-sm font-medium text-gray-700 mb-2">
            Plats *
          </label>
          <input
            id="location"
            v-model="form.location"
            type="text"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Ange plats"
          />
        </div>

        <!-- Ticket Types -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Biljettyper *
          </label>
          <div class="space-y-4">
            <div 
              v-for="(ticket, index) in form.ticketTypes" 
              :key="index"
              class="border border-gray-200 rounded-md p-4"
            >
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label :for="`ticket-name-${index}`" class="block text-sm font-medium text-gray-700 mb-1">
                    Namn
                  </label>
                  <input
                    :id="`ticket-name-${index}`"
                    v-model="ticket.name"
                    type="text"
                    required
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Biljettyp namn"
                  />
                </div>
                <div>
                  <label :for="`ticket-price-${index}`" class="block text-sm font-medium text-gray-700 mb-1">
                    Pris (SEK)
                  </label>
                  <input
                    :id="`ticket-price-${index}`"
                    v-model.number="ticket.price"
                    type="number"
                    min="0"
                    step="1"
                    required
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label :for="`ticket-quantity-${index}`" class="block text-sm font-medium text-gray-700 mb-1">
                    Antal
                  </label>
                  <input
                    :id="`ticket-quantity-${index}`"
                    v-model.number="ticket.quantity"
                    type="number"
                    min="1"
                    required
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="1"
                  />
                </div>
              </div>
              <button
                type="button"
                @click="removeTicketType(index)"
                class="mt-2 text-red-600 hover:text-red-800 text-sm font-medium"
              >
                Ta bort biljettyp
              </button>
            </div>
            <button
              type="button"
              @click="addTicketType"
              class="w-full py-2 px-4 border-2 border-dashed border-gray-300 rounded-md text-gray-600 hover:border-gray-400 hover:text-gray-800 transition-colors"
            >
              + Lägg till biljettyp
            </button>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-between pt-6">
          <button
            type="button"
            @click="deleteEvent"
            :disabled="deleting"
            class="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            <span v-if="deleting" class="animate-spin mr-2">⟳</span>
            Ta bort evenemang
          </button>
          <div class="flex space-x-4">
            <router-link
              :to="`/events/${eventId}`"
              class="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700 transition-colors"
            >
              Avbryt
            </router-link>
            <button
              type="submit"
              :disabled="saving"
              class="bg-indigo-600 text-white px-6 py-2 rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50"
            >
              <span v-if="saving" class="animate-spin mr-2">⟳</span>
              Spara ändringar
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';

const route = useRoute();
const router = useRouter();
const eventId = route.params.id;

const loading = ref(true);
const saving = ref(false);
const deleting = ref(false);
const error = ref('');

const form = ref({
  title: '',
  description: '',
  date: '',
  location: '',
  ticketTypes: []
});

const addTicketType = () => {
  form.value.ticketTypes.push({
    name: '',
    price: 0,
    quantity: 1
  });
};

const removeTicketType = (index) => {
  form.value.ticketTypes.splice(index, 1);
};

const fetchEvent = async () => {
  try {
    loading.value = true;
    error.value = '';
    
    const response = await axios.get(`/api/events/${eventId}`);
    const event = response.data;
    
    // Check if user owns this event
    const userResponse = await axios.get('/api/auth/session');
    const currentUser = userResponse.data.user;
    
    if (event.createdBy !== currentUser.userId && event.organizerId !== currentUser.userId) {
      error.value = 'Du har inte behörighet att redigera detta evenemang.';
      return;
    }
    
    // Format date for datetime-local input
    const eventDate = new Date(event.date);
    const localDate = new Date(eventDate.getTime() - eventDate.getTimezoneOffset() * 60000);
    
    form.value = {
      title: event.title,
      description: event.description,
      date: localDate.toISOString().slice(0, 16),
      location: event.location,
      ticketTypes: event.ticketTypes || []
    };
    
  } catch (err) {
    console.error('Error fetching event:', err);
    error.value = 'Kunde inte ladda evenemanget. Försök igen senare.';
  } finally {
    loading.value = false;
  }
};

const updateEvent = async () => {
  try {
    saving.value = true;
    error.value = '';
    
    const eventData = {
      title: form.value.title,
      description: form.value.description,
      date: form.value.date,
      location: form.value.location,
      ticketTypes: form.value.ticketTypes // Send as array, not JSON string
    };
    
    console.log('🔍 Sending event data:', eventData);
    
    await axios.put(`/api/events/${eventId}`, eventData);
    
    // Redirect to event details
    router.push(`/events/${eventId}`);
    
  } catch (err) {
    console.error('❌ Error updating event:', err);
    console.error('❌ Error response:', err.response?.data);
    error.value = 'Kunde inte uppdatera evenemanget. Försök igen senare.';
  } finally {
    saving.value = false;
  }
};

const deleteEvent = async () => {
  if (!confirm('Är du säker på att du vill ta bort detta evenemang? Detta går inte att ångra.')) {
    return;
  }
  
  try {
    deleting.value = true;
    error.value = '';
    
    await axios.delete(`/api/events/${eventId}`);
    
    // Show success message and redirect to events
    alert('Evenemanget har tagits bort framgångsrikt!');
    router.push('/events');
    
  } catch (err) {
    console.error('Error deleting event:', err);
    error.value = 'Kunde inte ta bort evenemanget. Försök igen senare.';
  } finally {
    deleting.value = false;
  }
};

const goBack = () => {
  router.go(-1);
};

onMounted(() => {
  fetchEvent();
});
</script>
